import './index.scss'

import { Navigate, useLocation, useParams } from 'react-router-dom'

import ErrorView from '@components/ErrorView'
import LoadingIndicator from '@components/LoadingIndicator'
import Monogram from '@components/Monogram'
import PageHeader from '@components/PageHeader'
import usePerson from '@hooks/usePerson'
import { PersonAttributes } from '@interfaces/person.interface'

// What a page linking here hands over: where Back should return to, and the
// name to show before the person loads. Neither survives a reload, so both
// fall back to sensible defaults.
export type RoutedPerson = {
  personName?: string;
  backLocation?: string;
};

export function personPagePath(personID: string) {
  return `/people/${personID}`;
}

// A person's details, at their own URL so the page can be reloaded or shared
// on its own. Always fetched by ID, so it never shows a stale copy.
export default function PersonPage() {
  const location = useLocation();
  const { personID } = useParams();
  const routedPerson = (location.state as RoutedPerson | null) ?? null;

  const { person, loading, error, refetch } = usePerson(personID);

  if (!personID) {
    return <Navigate to='/people' replace />
  }

  const name = person?.attributes.name || routedPerson?.personName || 'Person';

  return <div className='person-page'>
    <PageHeader
      title={name}
      backLocation={routedPerson?.backLocation ?? '/people'} />

    {loading && !person ? <div className='person-detail-state'>
      <LoadingIndicator fullScreen={false} />
    </div> : null}

    {error && !person ? <ErrorView
      error={error}
      buttonText='Retry'
      buttonAction={() => refetch()}
      fullHeight /> : null}

    {person ? <>
      <section className='person-identity-card'>
        <Monogram name={person.attributes.name} size='large' />

        <div className='person-identity-text'>
          <p className='name'>{person.attributes.name}</p>
          {person.attributes.email ? <p className='email'>{person.attributes.email}</p> : null}
        </div>
      </section>

      <PersonDetails attributes={person.attributes} />
    </> : null}
  </div>
}

// Phone numbers are typed by hand elsewhere, so only the shapes that are
// unambiguously North American get reformatted. Anything else -- an
// international number, an extension, a number with letters -- is shown exactly
// as it was entered rather than risking a wrong guess.
function formatPhoneNumber(phoneNumber?: string | null) {
  if (!phoneNumber) {
    return phoneNumber;
  }

  const digits = phoneNumber.replace(/\D/g, '');
  const isPlainNANP = /^[\d\s().+-]+$/.test(phoneNumber);

  if (!isPlainNANP) {
    return phoneNumber;
  }

  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }

  return phoneNumber;
}

// Birthdays arrive as ISO 8601. Formatting from the date parts in UTC avoids
// the day-earlier shift a date-only string picks up when parsed as local time
// west of Greenwich.
function formatBirthday(birthday?: string | null) {
  if (!birthday) {
    return birthday;
  }

  const parts = /^(\d{4})-(\d{2})-(\d{2})/.exec(birthday);

  if (!parts) {
    return birthday;
  }

  const [, year, month, day] = parts;
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));

  if (Number.isNaN(date.getTime())) {
    return birthday;
  }

  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC'
  });
}

function PersonDetails(props: { attributes: PersonAttributes }) {
  // Every field renders whether or not it's set, so a blank one reads as
  // "nothing on file" rather than as a field that doesn't exist.
  const rows = [
    { label: 'Email', value: props.attributes.email },
    { label: 'Phone', value: formatPhoneNumber(props.attributes.phoneNumber) },
    { label: 'Birthday', value: formatBirthday(props.attributes.birthday) },
    { label: 'Gender', value: props.attributes.gender },
    { label: 'Medical Notes', value: props.attributes.medicalNotes }
  ];

  return <dl className='person-detail-rows'>
    {rows.map((row) => {
      return <div key={row.label} className='person-detail-row'>
        <dt>{row.label}</dt>
        <dd className={row.value ? undefined : 'is-empty'}>{row.value || 'None'}</dd>
      </div>
    })}
  </dl>
}
